import {
  BLOTTER_SELECTION_MULTI_PARAMS_WITH_CHECKBOXES,
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  ExportMenuItems,
  FormControlSizes,
  HStack,
  IconButton,
  IconName,
  Toggle,
  Tooltip,
  columnTypes,
  getExpandCollapseMenus,
  getShowJSONContextItem,
  isGridApiReady,
  selectAll,
  useBlotterTable,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useDisclosureWithContext,
  useDynamicCallback,
  useGetDefaultContextMenuItems,
  useJsonModal,
  useMixpanel,
  usePersistedBlotterTable,
} from '@talos/kyoko';
import type {
  GetContextMenuItems,
  GetContextMenuItemsParams,
  MenuItemDef,
  RowSelectionOptions,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useTradingSettings } from '../../../../providers/TradingSettingsContext';
import { useLPSettlementContext } from '../LPSettlementProvider';
import { useSettleBatchContext } from '../SettleBatchContext';
import type { TradeSettlementColumn } from '../types';
import type { LPSettlementReportBlotterRow } from './blotter/lpSettlementReportRow';
import { NewManualBatchDialog } from './NewManualBatchDialog';
import { BlotterWrapper } from './styles';
import { useCanSettleSelectedTrades } from './useCanSettleSelectedTrades';
import { useTradeSettlementColumns } from './useTradeSettlementColumns';
import { useUnsettledTransactionsBlotterObs } from './useUnsettledTransactionsBlotterObs';

interface UnsettledTransactionsBlotterParams {
  blotterID: string;
  defaultColumns: TradeSettlementColumn[];
  portalID: string;
  batchID?: string;
}

export type NewManualBatchDisclosureContext = {
  selectedTrades: LPSettlementReportBlotterRow[];
};

export const UnsettledTransactionsBlotter = ({
  blotterID,
  defaultColumns: defaultBlotterColumns,
  portalID,
  batchID,
}: UnsettledTransactionsBlotterParams) => {
  const mixpanel = useMixpanel();
  const { showAllDecimalsInBlotters } = useTradingSettings();
  const { columns: defaultColumns, aggFuncs } = useTradeSettlementColumns({ defaultColumns: defaultBlotterColumns });
  const [selectedTrades, setSelectedTrades] = useState<LPSettlementReportBlotterRow[]>([]);
  const manualBatchDialog = useDisclosureWithContext<NewManualBatchDisclosureContext>();
  const { openBatchDetails } = useSettleBatchContext();
  const { showAllDecimals, setShowAllDecimals } = useLPSettlementContext();
  const { canSettle, hasEditLPSettlemenyRole, reason: cannotSettleReason } = useCanSettleSelectedTrades(selectedTrades);

  const readOnly = batchID != null;

  const persisted = usePersistedBlotterTable(blotterID);

  const { handleClickJson, jsonModal } = useJsonModal();
  const getDefaultContextMenuItems = useGetDefaultContextMenuItems();

  const getContextMenuItems: GetContextMenuItems<LPSettlementReportBlotterRow> = useDynamicCallback(
    (params: GetContextMenuItemsParams<LPSettlementReportBlotterRow>) => {
      const items: (MenuItemDef | 'separator')[] = [
        ...getExpandCollapseMenus(params),
        'separator',
        selectAll(params, mixpanel),
        'separator',
        ...getDefaultContextMenuItems(params),
      ];

      const rightClickedTrade = params.node?.data;
      if (rightClickedTrade != null) {
        items.push('separator');
        items.push(getShowJSONContextItem({ params, handleClickJson }));
      }

      return compact(items);
    }
  );

  const handleRowSelectionChanged = useCallback(({ api }: SelectionChangedEvent<LPSettlementReportBlotterRow>) => {
    if (!isGridApiReady(api)) {
      return;
    }
    // getSelectedRows() only returns rows that pass isRowSelectable, which already filters out group rows
    const selectedRows = api.getSelectedRows();
    setSelectedTrades(selectedRows);
  }, []);

  const { lpSettlementReportsRowObservable, errorObservable } = useUnsettledTransactionsBlotterObs(batchID);

  const autoGroupColumnDef = useMemo(
    () =>
      columnTypes.group({
        type: 'group',
        editable: false,
        hide: false,
        sortable: true,
        suppressColumnsToolPanel: false,
        pinned: 'left',
      }),
    []
  );

  const rowSelection = useMemo<RowSelectionOptions>(
    () => ({
      ...BLOTTER_SELECTION_MULTI_PARAMS_WITH_CHECKBOXES,
      checkboxes: !readOnly && hasEditLPSettlemenyRole,
      headerCheckbox: !readOnly && hasEditLPSettlemenyRole,
      hideDisabledCheckboxes: true,
      groupSelects: 'filteredDescendants',
    }),
    [readOnly, hasEditLPSettlemenyRole]
  );

  const blotterTableSettings = useMemo(
    () => ({
      showAllDecimals: showAllDecimals ?? false,
    }),
    [showAllDecimals]
  );

  const blotterTable = useBlotterTable<LPSettlementReportBlotterRow>({
    dataObservable: lpSettlementReportsRowObservable,
    errorObservable,
    columns: defaultColumns,
    rowID: 'RowID' satisfies keyof LPSettlementReportBlotterRow,
    persistence: persisted,
    settings: blotterTableSettings,
    gridOptions: {
      showOpenedGroup: true,
      groupRemoveLowestSingleChildren: true,
      autoGroupColumnDef,
      rowGroupPanelShow: 'always',
      groupDisplayType: 'singleColumn',
      suppressRowGroupHidesColumns: false,
      aggFuncs,
      onSelectionChanged: handleRowSelectionChanged,
      rowSelection,
      selectionColumnDef: { pinned: 'left', width: 32 },
      getContextMenuItems,
    },
    exportOptions: {
      name: 'Unsettled Transactions',
    },
  });

  const { expandAllGroups, collapseAllGroups } = blotterTable;
  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleSettleClick = useCallbackRef(() => {
    manualBatchDialog.open({
      selectedTrades,
    });
  });

  const handleSubmit = useCallback(
    (batchID: string) => {
      openBatchDetails(batchID);
      manualBatchDialog.close();
    },
    [openBatchDetails, manualBatchDialog]
  );

  return (
    <>
      <BlotterTableFilters
        {...blotterTable.blotterTableFiltersProps}
        portalId={portalID}
        quickFilterPrefix={
          <HStack gap="spacing8">
            {hasEditLPSettlemenyRole && <span>Total Transactions: {selectedTrades.length}</span>}
            {!readOnly && (
              <Tooltip tooltip={cannotSettleReason}>
                <Button
                  size={FormControlSizes.Small}
                  disabled={!canSettle}
                  onClick={handleSettleClick}
                  startIcon={IconName.Plus}
                  data-testid="preview-settlement-button"
                  variant={ButtonVariants.Positive}
                >
                  New Manual Batch
                </Button>
              </Tooltip>
            )}
          </HStack>
        }
        suffix={
          <>
            <IconButton
              icon={IconName.ListExpand}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Default}
              onClick={expandAllGroups}
              data-testid="expand-all-button"
            />
            <IconButton
              icon={IconName.ListCollapse}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Default}
              onClick={collapseAllGroups}
            />
            <BlotterTableExtrasMenu {...extrasMenuPopover}>
              {!showAllDecimalsInBlotters && (
                <Toggle
                  size={FormControlSizes.Small}
                  checked={showAllDecimals}
                  onChange={setShowAllDecimals}
                  label="Show All Decimals"
                  data-testid="show-all-decimals-toggle"
                />
              )}
              <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
            </BlotterTableExtrasMenu>
          </>
        }
      />
      <BlotterWrapper data-testid="unsettled-transactions-blotter">
        <BlotterTable {...blotterTable} />
      </BlotterWrapper>
      {jsonModal}
      <NewManualBatchDialog onSubmit={handleSubmit} {...manualBatchDialog} />
    </>
  );
};
